/**
 * CaptainsPage.js - Dedicated page for managing captains (user-scoped)
 */

import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';
import './CaptainsPage.css';

const CAPTAINS_PER_PAGE = 50;

function CaptainsPage({ onBack }) {
  const [captains, setCaptains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name'); // 'name' | 'team' | 'recent'
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState(null);

  // Add form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [addLoading, setAddLoading] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [editLoading, setEditLoading] = useState(false);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchCaptains = useCallback(async () => {
    try {
      const result = await api.getCaptains();
      setCaptains(result.captains);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchCaptains();
  }, [fetchCaptains]);

  const handleCreate = async () => {
    if (!addForm.name.trim()) return;
    setAddLoading(true);
    try {
      const result = await api.createCaptain({
        name: addForm.name.trim(),
        email: addForm.email.trim() || null,
        phone: addForm.phone.trim() || null,
        notes: addForm.notes.trim() || null
      });
      setCaptains(prev => [...prev, result.captain].sort((a, b) => a.name.localeCompare(b.name)));
      setAddForm({ name: '', email: '', phone: '', notes: '' });
      setShowAddForm(false);
      showToast('Captain created');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setAddLoading(false);
    }
  };

  const handleEdit = (captain) => {
    setEditingId(captain.id);
    setEditForm({
      name: captain.name,
      email: captain.email || '',
      phone: captain.phone || '',
      notes: captain.notes || ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) return;
    setEditLoading(true);
    try {
      const result = await api.updateCaptain(editingId, {
        name: editForm.name.trim(),
        email: editForm.email.trim() || null,
        phone: editForm.phone.trim() || null,
        notes: editForm.notes.trim() || null
      });
      setCaptains(prev =>
        prev.map(c => c.id === editingId ? { ...c, ...result.captain } : c)
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setEditingId(null);
      showToast('Captain updated');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (captain) => {
    if (!window.confirm(`Delete captain "${captain.name}"?`)) return;
    try {
      await api.deleteCaptain(captain.id);
      setCaptains(prev => prev.filter(c => c.id !== captain.id));
      showToast('Captain deleted');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const filtered = captains.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      (c.email && c.email.toLowerCase().includes(q)) ||
      (c.teams && c.teams.some(t => t.name.toLowerCase().includes(q) || (t.leagueName && t.leagueName.toLowerCase().includes(q))))
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'team') {
      const aTeam = a.teams?.[0]?.name || '';
      const bTeam = b.teams?.[0]?.name || '';
      return aTeam.localeCompare(bTeam) || a.name.localeCompare(b.name);
    }
    if (sortBy === 'recent') {
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    }
    return a.name.localeCompare(b.name);
  });

  const totalPages = Math.ceil(sorted.length / CAPTAINS_PER_PAGE);
  const paginated = sorted.length > CAPTAINS_PER_PAGE
    ? sorted.slice((currentPage - 1) * CAPTAINS_PER_PAGE, currentPage * CAPTAINS_PER_PAGE)
    : sorted;

  // Reset to page 1 when search/sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortBy]);

  if (loading) {
    return (
      <div className="captains-container">
        <p style={{ textAlign: 'center', color: 'var(--color-text-secondary)' }}>Loading captains...</p>
      </div>
    );
  }

  return (
    <div className="captains-container">
      <button className="captains-back" onClick={onBack}>&larr; Dashboard</button>

      <div className="captains-header">
        <h2>
          Captains
          <span className="captains-count">{captains.length}</span>
        </h2>
        <button
          className="captains-btn-primary"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : '+ Add Captain'}
        </button>
      </div>

      {showAddForm && (
        <div className="captains-add-form">
          <h3>New Captain</h3>
          <div className="captains-form-grid">
            <input
              placeholder="Name *"
              value={addForm.name}
              onChange={e => setAddForm(prev => ({ ...prev, name: e.target.value }))}
            />
            <input
              placeholder="Email"
              type="email"
              value={addForm.email}
              onChange={e => setAddForm(prev => ({ ...prev, email: e.target.value }))}
            />
            <input
              placeholder="Phone"
              value={addForm.phone}
              onChange={e => setAddForm(prev => ({ ...prev, phone: e.target.value }))}
            />
            <input
              placeholder="Notes (e.g., available Tuesdays)"
              value={addForm.notes}
              onChange={e => setAddForm(prev => ({ ...prev, notes: e.target.value }))}
            />
          </div>
          <div className="captains-form-actions">
            <button
              className="captains-btn-primary"
              onClick={handleCreate}
              disabled={!addForm.name.trim() || addLoading}
            >
              {addLoading ? 'Creating...' : 'Create Captain'}
            </button>
          </div>
        </div>
      )}

      {captains.length > 3 && (
        <div className="captains-toolbar">
          <div className="captains-search">
            <input
              placeholder="Search by name, email, or team..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')}>Clear</button>
            )}
          </div>
          <div className="captains-sort">
            <span className="captains-sort-label">Sort:</span>
            {[
              { key: 'name', label: 'Name' },
              { key: 'team', label: 'Team' },
              { key: 'recent', label: 'Recent' },
            ].map(opt => (
              <button
                key={opt.key}
                className={`captains-sort-btn${sortBy === opt.key ? ' active' : ''}`}
                onClick={() => setSortBy(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {sorted.length === 0 ? (
        <div className="captains-empty">
          {captains.length === 0 ? (
            <>
              <p><strong>No captains yet</strong></p>
              <p>Add captains here and assign them to teams when creating leagues.</p>
            </>
          ) : (
            <p>No captains found matching "{searchQuery}"</p>
          )}
        </div>
      ) : (
        <div className="captains-list">
          {paginated.map(captain => (
            <div key={captain.id} className="captains-card">
              {editingId === captain.id ? (
                /* Edit mode */
                <div>
                  <div className="captains-form-grid">
                    <input
                      placeholder="Name *"
                      value={editForm.name}
                      onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    />
                    <input
                      placeholder="Email"
                      type="email"
                      value={editForm.email}
                      onChange={e => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                    />
                    <input
                      placeholder="Phone"
                      value={editForm.phone}
                      onChange={e => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                    />
                    <input
                      placeholder="Notes"
                      value={editForm.notes}
                      onChange={e => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                    />
                  </div>
                  <div className="captains-form-actions" style={{ marginTop: '0.5rem' }}>
                    <button
                      className="captains-btn-primary"
                      onClick={handleSaveEdit}
                      disabled={!editForm.name.trim() || editLoading}
                    >
                      {editLoading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="captains-btn-secondary"
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                /* Display mode */
                <div className="captains-card-header">
                  <div className="captains-card-info">
                    <p className="captains-card-name">{captain.name}</p>
                    {captain.email && (
                      <p className="captains-card-detail">{captain.email}</p>
                    )}
                    {captain.phone && (
                      <p className="captains-card-detail">{captain.phone}</p>
                    )}
                    {captain.notes && (
                      <p className="captains-card-notes">{captain.notes}</p>
                    )}
                    {captain.teams && captain.teams.length > 0 && (
                      <div className="captains-card-teams">
                        <div className="captains-card-teams-label">Assigned to</div>
                        {captain.teams.map(t => (
                          <span key={t.id} className="captains-team-tag">
                            {t.name} ({t.leagueName})
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="captains-card-actions">
                    <button onClick={() => handleEdit(captain)}>Edit</button>
                    <button className="captains-btn-delete" onClick={() => handleDelete(captain)}>Delete</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="captains-pagination">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            &larr; Prev
          </button>
          <span className="captains-pagination-info">
            Page {currentPage} of {totalPages} ({sorted.length} captains)
          </span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            Next &rarr;
          </button>
        </div>
      )}

      {toast && (
        <div className={`captains-toast ${toast.type}`}>{toast.message}</div>
      )}
    </div>
  );
}

export default CaptainsPage;
